/**
 * Neutral-landing decision — miniplan H2 (D-2/D-3),
 * `ai-docs/lexide-home-flow-redesign-miniplan.md`.
 *
 * A returning lawyer should land in the conversation they left, not on a
 * launcher. This module answers the one question that decides it:
 *
 *   "Has this lawyer ever done anything, and if so, where were they?"
 *
 * Pure + React-free so the answer is unit-testable without a Router mount or a
 * network stub. The component half (`LastActiveContextSync`) supplies the
 * inputs and performs the navigate.
 *
 * ## Why the signal is a UNION of two sources
 *
 * (Since H3-part-3 the app never RESTS on a neutral path — `/` and `/home`
 * redirect to `/chat` immediately — so the boot path, not the current one, is
 * what makes a landing neutral. See `isUnclaimedLandingPath`.)
 *
 * The server record (`/api/lx/context`) is the strong signal — cross-device,
 * and written by BOTH the SPA (case/matter/client selections) and the runtime's
 * IM scope-selection (`im-scope-selection.ts::syncContext`, which persists
 * `global` too). But there is a hole: the SPA deliberately NEVER writes
 * `global` (writing it on a mere `/home` or `/chat` visit would wipe the
 * last-active CASE the resume chip surfaces — see `LastActiveContextSync`), so
 * a lawyer who has only ever used the LexIDE global chat leaves the server
 * record at its synthesised epoch default and would read as first-run forever.
 *
 * The local stamp closes exactly that hole, and it is also the most literal
 * reading of the operator's rule (2026-07-23): *"once ever, and repeat until
 * user makes some kind of activity — if he exits without typing anything, next
 * time it should show welcome again."* It is stamped on SEND, not on visit.
 *
 * ## The residual gap, stated rather than hidden
 *
 * A lawyer whose only activity has ever been LexIDE global chat, opening a
 * BRAND-NEW browser profile, sees the welcome one more time. Accepted
 * deliberately: the alternative is gating the landing on a gateway round-trip
 * for `lx.chat_history` (the only other place that fact lives), which would
 * make the first paint depend on the runtime container being awake. A welcome
 * screen with a working composer is a small cost; a landing that stalls behind
 * a cold runtime is not.
 */

import type { UserContextRecord } from "@/lib/lexserver-client";
import type { ActiveSelection } from "@/stores/useActiveContextStore";
import { isResumableCaseRecord } from "@/lib/last-active-context";
import { chatTargetForContext } from "@/lib/chat-left-routes";

/**
 * `welcome` — no resume is owed; the boot redirect's destination (`/chat`)
 *             stands. Since H3-part-3 retired the welcome screen this verdict
 *             no longer routes anywhere of its own — it is still computed
 *             because it is the negative half of "has this lawyer ever done
 *             anything", which the resume decision below reads.
 * `resume`  — redirect to `to`, replacing the history entry so Back does not
 *             bounce the lawyer straight back into the redirect.
 */
export type LandingDecision =
  | { kind: "welcome" }
  | { kind: "resume"; to: string };

/**
 * Is this a NEUTRAL landing — a path that expresses no intent of its own, and
 * may therefore be replaced by the lawyer's last context?
 *
 * Only `/` and a bare `/home`. `/home/:conversationId` is EXCLUDED: it carries
 * an id, which makes it a deep link, and D-2 is explicit that deep links are
 * never overridden.
 */
export function isNeutralLandingPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/" || p === "/home";
}

/**
 * Has the lawyer claimed a destination of their own since boot?
 *
 * `isNeutralLandingPath` answers "may this path be replaced" — but since
 * H3-part-3 retired the welcome screen, NOTHING stays on a neutral path:
 * `App.tsx` replaces `/`, `/home` and the catch-all with `/chat` on the first
 * render pass, while the landing verdict is still a network round-trip away.
 * Judging neutrality on the path at VERDICT time therefore always answered
 * "not neutral", and D-2's active resume silently stopped firing — a returning
 * lawyer with a case record landed on `/chat` instead of their workspace, with
 * every unit test still green because the pure predicate was never wrong about
 * the paths it was asked about.
 *
 * So the BOOT path decides whether a resume was ever on the table, and this
 * decides whether the lawyer has since claimed something else. `/chat` counts
 * as unclaimed precisely because it is where the retirement redirect PUTS
 * them; any other path (a case workspace, a deep link) is an expressed intent
 * and outranks the stored record, exactly as D-2 requires.
 */
export function isUnclaimedLandingPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return isNeutralLandingPath(p) || p === "/chat";
}

/**
 * Has the server record ever been written for real? A fresh user gets a
 * synthesised `global` record stamped with `new Date(0)`; anything later than
 * the epoch means somebody — the SPA or the runtime's IM resolver — persisted a
 * real context at some point.
 *
 * An unparseable `updatedAt` yields `NaN`, which fails the `> 0` comparison and
 * therefore reads as "no activity" — the conservative direction (show the
 * welcome) rather than redirecting on a value we could not read.
 */
export function hasServerActivity(ctx: UserContextRecord | null): boolean {
  return ctx != null && new Date(ctx.updatedAt).getTime() > 0;
}

/**
 * Resolve where a neutral landing should go.
 *
 * Only a full CASE record earns a workspace redirect; every other scope that
 * shows real activity resolves to the global conversation. That is deliberate
 * parity with the passive resume this replaces (which also only ever seeded
 * cases) — a half-formed `matter` record does not identify a conversation
 * surface, and `/chat` is always a valid one.
 */
export function resolveLanding(args: {
  ctx: UserContextRecord | null;
  /** Has this browser seen the lawyer SEND something? (persisted stamp) */
  hasLocalActivity: boolean;
}): LandingDecision {
  const { ctx, hasLocalActivity } = args;

  if (isResumableCaseRecord(ctx)) {
    // `chatTargetForContext` is the established idiom for "the conversation
    // surface of this context" and already builds the short-URL case route the
    // resolver bootstraps parents from. Reusing it keeps the landing target and
    // the focus-pane target from drifting apart.
    return {
      kind: "resume",
      to: chatTargetForContext({
        scope: "case",
        clientId: ctx.clientId,
        matterId: ctx.matterId,
        caseId: ctx.caseId,
      }),
    };
  }

  if (hasServerActivity(ctx) || hasLocalActivity) return { kind: "resume", to: "/chat" };

  return { kind: "welcome" };
}

/**
 * Where the rail's **Home** entry goes (D-3). "A returning lawyer pressing Home
 * wants *my desk*, not a launcher."
 *
 * Mid-session there is no server round-trip to make, so this reads the live
 * selection first (it is fresher than any record) and falls back to the boot
 * verdict for the case the selection cannot answer: a returning lawyer whose
 * only activity lives in the SERVER record has nothing client-side to derive
 * from, and without the verdict their own Home button would deposit them on a
 * first-run welcome.
 *
 * A first-run lawyer goes to `/chat` like everyone else — H3-part-3 retired
 * the welcome screen, so there is no first-run destination left to branch to.
 * The `landing` argument is consequently unread today; it is kept in the
 * signature because the verdict is still the honest input to this question and
 * both call sites already pass it, so re-branching on it (if a first-run
 * surface ever returns) costs nothing.
 *
 * The H3 note this comment used to carry — "Home and the rail's Chat entry
 * coincide for every lawyer with no case selected" — was RESOLVED in two
 * halves: H3-part-1 removed the Chat rail entry, and H3-part-3 settled the
 * remaining destination on `/chat`.
 */
export function homeTarget(args: {
  selection: ActiveSelection | null;
  landing: "welcome" | "resume" | null;
}): string {
  // Same delegation as `resolveLanding`, for the same reason: one definition of
  // "the conversation surface of this context". Hand-building the route here
  // would be exactly the drift that comment warns about.
  if (args.selection?.caseId) return chatTargetForContext(args.selection);
  // H3 RESOLVED (operator 2026-08-04): the welcome screen is retired — Home
  // ALWAYS goes to the global conversation, first-run included. This is the
  // redundancy the H3 note above predicted ("Home and the rail's Chat entry
  // coincide"), settled in favour of one destination.
  //
  // Why it matters beyond tidiness: `/home` is not a passive screen. HomeLayout
  // runs `setSidePanelCollapsed(true)` on every render as its "welcome focus
  // mode", so merely PASSING THROUGH it collapses the workspace navigator for
  // whoever lands there — the same pair of defects `d6f8e0207` fixed for the
  // exit-case path, which reappeared here because first-run still routed home.
  return "/chat";
}
